import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Box, Drawer } from '@mui/material';
import {
  useMenuSliderToggle,
  useMenuTocToggle,
  useMenuEditLeftToggle,
  useMenuEditRightToggle,
  useMenuEditLeftMode,
  useSetMenuSliderToggle,
  useSetMenuTocToggle,
  useSetMenuEditLeftToggle,
  useSetMenuEditRightToggle,
  useSetMenuEditLeftMode,
  useSetEditRightPanelMode,
} from '../../features/global/hooks';
import { PANEL_MODES } from '../../features/editCourse/editCourseState';
import { SimpleButton, theme } from '../../ui';
import { toRgba } from '../../ui/theme/colors';
import AppMenu from './AppMenu';
import TocMenu from '../../features/viewCourse/components/TocMenu';
import EditCourseLeftPanel from './EditCourseLeftPanel';
import EditCourseRightPanel from './EditCourseRightPanel';

const MenuWrapper = styled.div`
  display: ${(props) => (props.$visible ? 'inherit' : 'none')};
`;
const DrawerContainer = styled.div`
  position: relative;
  width: 300px;
  height: 100%;
`;
const FloatingToggle = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
  background: ${theme.colors.NEU_0}A3;
  border-radius: 4px;
  box-shadow: 0 2px 8px ${toRgba(theme.colors.NEU_999, 0.15)};
`;
const ContentBox = styled(Box)`
  height: 100%;
  overflow-y: auto;
`;

const MenuSlider = () => {
  const { t } = useTranslation();
  const setMenuSliderToggle = useSetMenuSliderToggle();
  const setMenuTocToggle = useSetMenuTocToggle();
  const setMenuEditLeftToggle = useSetMenuEditLeftToggle();
  const setMenuEditRightToggle = useSetMenuEditRightToggle();
  const setMenuEditLeftMode = useSetMenuEditLeftMode();
  const setEditRightPanelMode = useSetEditRightPanelMode();

  const menuSliderToggle = useMenuSliderToggle();
  const menuTocToggle = useMenuTocToggle();
  const menuEditLeftToggle = useMenuEditLeftToggle();
  const menuEditRightToggle = useMenuEditRightToggle();
  const menuEditLeftMode = useMenuEditLeftMode();
  const isStructureMode = menuEditLeftMode === PANEL_MODES.STRUCTURE;

  const toggleDrawer = (open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setMenuSliderToggle(open);
    setMenuTocToggle(open);
    setMenuEditLeftToggle(open);
    setMenuEditRightToggle(open);
    if (!open) setEditRightPanelMode('module');
  };

  const toggleMode = useCallback(
    (e) => {
      e.stopPropagation();
      const newMode = isStructureMode ? PANEL_MODES.TOC : PANEL_MODES.STRUCTURE;
      setMenuEditLeftMode(newMode);
    },
    [isStructureMode, setMenuEditLeftMode],
  );

  return (
    <div>
      <Drawer anchor={'left'} open={menuSliderToggle || menuTocToggle || menuEditLeftToggle || menuEditRightToggle} onClose={toggleDrawer(false)}>
        <DrawerContainer>
          {/* Floating toggle button - only for edit left panel */}
          {menuEditLeftToggle && (
            <FloatingToggle>
              <SimpleButton
                icon={isStructureMode ? 'Visible' : 'Edit'}
                onClick={toggleMode}
                variant='ghost'
                square
                size='small'
                tooltip={isStructureMode ? t('editCourse.outlineView') : t('editCourse.structureView')}
                aria-label={isStructureMode ? t('editCourse.outlineView') : t('editCourse.structureView')}
              />
            </FloatingToggle>
          )}

          <ContentBox role='presentation' onClick={toggleDrawer(false)} onKeyDown={toggleDrawer(false)}>
            <MenuWrapper $visible={menuSliderToggle}>
              <AppMenu />
            </MenuWrapper>
            <MenuWrapper $visible={menuTocToggle}>
              <TocMenu />
            </MenuWrapper>
            <MenuWrapper $visible={menuEditLeftToggle}>
              <EditCourseLeftPanel />
            </MenuWrapper>
            <MenuWrapper $visible={menuEditRightToggle}>
              <EditCourseRightPanel />
            </MenuWrapper>
          </ContentBox>
        </DrawerContainer>
      </Drawer>
    </div>
  );
};

export default MenuSlider;
